import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { ArrowLeftRight, AlertTriangle, Sparkles } from "lucide-react";

/** Iter168: Rebook-Dialog (Umbuchen).
 *
 *  Öffnet sich für einen verbuchten Beleg und erlaubt es, das Aufwandskonto
 *  in einem Klick zu ändern. Backend erledigt Storno + Neubuchung.
 *
 *  Props:
 *    - receipt: Beleg-Objekt mit _id + account_id + status="booked"
 *    - open, onClose, onDone
 *    - companyId
 */
export function RebookDialog({ receipt, open, onClose, onDone, companyId }) {
  const [accounts, setAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedCostCenter, setSelectedCostCenter] = useState("");
  const [note, setNote] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  const askAi = async () => {
    if (!aiText.trim() || aiLoading) return;
    setAiLoading(true);
    setAiSuggestions([]);
    try {
      const r = await apiClient.post(`/companies/${companyId}/accounts/ai-suggest`, {
        text: aiText.trim(),
      });
      const items = r.data || [];
      setAiSuggestions(items);
      if (items.length === 0) toast.info("Kein passendes Konto gefunden — bitte anders beschreiben.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Vorschlag fehlgeschlagen");
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !companyId) return;
    setSearch("");
    setNote("");
    setSelectedAccount("");
    setSuggestions([]);
    setAiText("");
    setAiSuggestions([]);
    setSelectedCostCenter(receipt?.cost_center_id || "");
    setLoading(true);
    Promise.all([
      apiClient.get(`/companies/${companyId}/accounts`),
      apiClient.get(`/companies/${companyId}/cost-centers`).catch(() => ({ data: [] })),
    ])
      .then(([accRes, ccRes]) => {
        setAccounts(accRes.data || []);
        setCostCenters(ccRes.data || []);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Konten konnten nicht geladen werden");
      })
      .finally(() => setLoading(false));
    if (receipt?._id) {
      setSuggestionsLoading(true);
      apiClient
        .get(`/receipts/${receipt._id}/rebook-suggest`)
        .then((r) => setSuggestions(r.data?.suggestions || []))
        .catch(() => setSuggestions([]))
        .finally(() => setSuggestionsLoading(false));
    }
  }, [open, companyId, receipt?._id, receipt?.cost_center_id]);

  // Aktuelles Konto (aus Beleg) suchen
  const currentAccount = useMemo(() => {
    if (!receipt?.account_id || !accounts.length) return null;
    return accounts.find(
      (a) =>
        a._id === receipt.account_id ||
        a.number === receipt.account_id ||
        a.code === receipt.account_id,
    );
  }, [receipt?.account_id, accounts]);

  // Nur Aufwands-Konten (4xxx-8xxx) sind sinnvoll für Umbuchung
  const filteredAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accounts
      .filter((a) => {
        const code = String(a.number || a.code || "");
        // Fokus auf Aufwands-/Ertrags-/Neutrale Konten (nicht Bank/Kasse)
        if (code && (code.startsWith("10") || code.startsWith("11"))) {
          return false;
        }
        return true;
      })
      .filter((a) => {
        if (!q) return true;
        const label = `${a.number || a.code || ""} ${a.name || ""}`.toLowerCase();
        return label.includes(q);
      })
      .slice(0, 200);
  }, [accounts, search]);

  const doRebook = async () => {
    if (!selectedAccount) {
      toast.error("Bitte neues Konto wählen.");
      return;
    }
    if (!receipt?._id) return;
    setExecuting(true);
    try {
      await apiClient.post(`/receipts/${receipt._id}/rebook`, {
        new_account_id: selectedAccount,
        cost_center_id: selectedCostCenter || null,
        note: note || null,
      });
      const accNew = accounts.find(
        (a) => a._id === selectedAccount || a.number === selectedAccount || a.code === selectedAccount,
      );
      toast.success(
        `Beleg umgebucht auf ${accNew?.number || accNew?.code || selectedAccount} ${accNew?.name || ""}`,
      );
      toast.info("KI lernt aus deiner Korrektur und verfeinert die Konto-Beschreibung.", { duration: 5000 });
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Umbuchen fehlgeschlagen");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="rebook-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-violet-600" />
            Beleg umbuchen
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="p-6 text-center text-sm text-slate-500">
            Lade Konten…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                Die Original-Buchung wird per Storno rückgängig gemacht und der
                Beleg mit dem neuen Konto sofort neu gebucht. Die Historie
                bleibt vollständig erhalten (GeBüV-konform).
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs text-slate-500 mb-1">Aktuelles Konto</div>
                {currentAccount ? (
                  <div className="font-mono">
                    <span className="font-semibold">
                      {currentAccount.number || currentAccount.code}
                    </span>{" "}
                    <span className="text-slate-700">{currentAccount.name}</span>
                  </div>
                ) : (
                  <div className="text-slate-400 italic">
                    {receipt?.account_id || "—"}
                  </div>
                )}
              </div>
              <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
                <div className="text-xs text-violet-600 mb-1">Neues Konto</div>
                {selectedAccount ? (
                  (() => {
                    const a = accounts.find(
                      (x) =>
                        x._id === selectedAccount ||
                        x.number === selectedAccount ||
                        x.code === selectedAccount,
                    );
                    return (
                      <div className="font-mono">
                        <span className="font-semibold">
                          {a?.number || a?.code}
                        </span>{" "}
                        <span className="text-slate-700">{a?.name}</span>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-violet-400 italic">wähle unten…</div>
                )}
              </div>
            </div>

            {(suggestionsLoading || suggestions.length > 0) && (
              <div data-testid="rebook-suggestions">
                <Label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-violet-600" />
                  KI-Empfehlung
                </Label>
                {suggestionsLoading ? (
                  <div className="text-xs text-slate-400 italic py-1">
                    Analysiere Buchungshistorie…
                  </div>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    {suggestions.map((s) => {
                      const val = s.account_id || s.code;
                      const isSelected =
                        selectedAccount === val || selectedAccount === s.code;
                      return (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setSelectedAccount(val)}
                          className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition flex items-center justify-between gap-2 ${
                            isSelected
                              ? "border-violet-400 bg-violet-100"
                              : "border-violet-200 bg-violet-50/50 hover:bg-violet-50"
                          }`}
                          data-testid={`rebook-suggestion-${s.code}`}
                        >
                          <div className="min-w-0">
                            <span className="font-mono font-semibold">{s.code}</span>{" "}
                            <span className="text-slate-700">{s.name || ""}</span>
                            <div className="text-xs text-slate-500 truncate">
                              {s.reason}
                            </div>
                          </div>
                          <Badge
                            variant="outline"
                            className="text-[10px] border-violet-300 text-violet-700 flex-shrink-0"
                          >
                            {Math.round((s.confidence || 0) * 100)}%
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div data-testid="rebook-ai-freetext">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-violet-600" />
                In eigenen Worten beschreiben
              </Label>
              <div className="flex gap-2">
                <Input
                  value={aiText}
                  onChange={(e) => setAiText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); askAi(); } }}
                  placeholder='z.B. "Marketing", "Kundenessen", "Benzin"…'
                  data-testid="rebook-ai-text-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={askAi}
                  disabled={aiLoading || !aiText.trim()}
                  className="text-violet-700 border-violet-300 hover:bg-violet-50 flex-shrink-0"
                  data-testid="rebook-ai-ask-btn"
                >
                  {aiLoading ? "Frage KI…" : "KI fragen"}
                </Button>
              </div>
              {aiSuggestions.length > 0 && (
                <div className="flex flex-col gap-1.5 mt-2" data-testid="rebook-ai-suggestions">
                  {aiSuggestions.map((s) => {
                    const isSelected = selectedAccount === s.code;
                    return (
                      <button
                        key={s.code}
                        type="button"
                        onClick={() => setSelectedAccount(s.code)}
                        className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition flex items-center justify-between gap-2 ${
                          isSelected
                            ? "border-violet-400 bg-violet-100"
                            : "border-violet-200 bg-violet-50/50 hover:bg-violet-50"
                        }`}
                        data-testid={`rebook-ai-suggestion-${s.code}`}
                      >
                        <div className="min-w-0">
                          <span className="font-mono font-semibold">{s.code}</span>{" "}
                          <span className="text-slate-700">{s.name}</span>
                          <div className="text-xs text-slate-500 truncate">{s.reason}</div>
                        </div>
                        <Badge
                          variant="outline"
                          className="text-[10px] border-violet-300 text-violet-700 flex-shrink-0"
                        >
                          {Math.round((s.confidence || 0) * 100)}%
                        </Badge>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1 block">
                Konto suchen
              </Label>
              <Input
                placeholder="z. B. 6210 oder Werbung…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="rebook-account-search"
              />
            </div>

            <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200">
              {filteredAccounts.length === 0 && (
                <div className="p-4 text-sm text-slate-500 text-center">
                  Keine Konten gefunden.
                </div>
              )}
              {filteredAccounts.map((a) => {
                const aKey = a._id || a.number || a.code;
                const curKey = currentAccount
                  ? currentAccount._id || currentAccount.number || currentAccount.code
                  : null;
                const isCurrent = !!curKey && aKey === curKey;
                const isSelected =
                  a._id === selectedAccount ||
                  a.number === selectedAccount ||
                  a.code === selectedAccount;
                const value = a._id || a.number || a.code;
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={isCurrent}
                    onClick={() => setSelectedAccount(value)}
                    className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 text-sm flex items-center justify-between transition ${
                      isSelected
                        ? "bg-violet-100"
                        : isCurrent
                        ? "bg-slate-50 opacity-60 cursor-not-allowed"
                        : "hover:bg-slate-50"
                    }`}
                    data-testid={`rebook-account-option-${a.number || a.code || a._id}`}
                  >
                    <div>
                      <span className="font-mono font-semibold">
                        {a.number || a.code}
                      </span>{" "}
                      <span className="text-slate-700">{a.name}</span>
                    </div>
                    {isCurrent && (
                      <Badge variant="outline" className="text-[10px]">
                        aktuell
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>

            {costCenters.length > 0 && (
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1 block">
                  Kostenstelle (optional)
                </Label>
                <select
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  value={selectedCostCenter}
                  onChange={(e) => setSelectedCostCenter(e.target.value)}
                  data-testid="rebook-cost-center"
                >
                  <option value="">— keine —</option>
                  {costCenters.map((cc) => (
                    <option key={cc._id || cc.id} value={cc._id || cc.id}>
                      {cc.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1 block">
                Grund / Notiz (optional)
              </Label>
              <Textarea
                placeholder="z. B. Falsches Aufwandskonto — korrigiert auf Werbung"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                data-testid="rebook-note"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="ghost" onClick={onClose} data-testid="rebook-cancel">
                Abbrechen
              </Button>
              <Button
                onClick={doRebook}
                disabled={executing || !selectedAccount}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="rebook-confirm"
              >
                {executing ? "Umbuche…" : "Umbuchen"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default RebookDialog;
